/**
 * Minimal, dependency-free semver comparison — just enough for compatibility checks
 * (docs/108-BACKWARD-COMPATIBILITY.md) and content-update diffing
 * (docs/107-CATALOG-UPDATE-DETECTION.md). Not a full semver implementation: no
 * pre-release/build-metadata ordering, just `major.minor.patch`. Malformed input is
 * handled safely (never throws) rather than rejected — a version string this parser
 * can't read is treated as `0.0.0`, so a comparison against it still resolves rather
 * than crashing whatever UI/logic called it.
 */

/** Parse a leading "x.y.z" out of `v`; unparsable input yields `[0, 0, 0]`. */
function parseParts(v: string): [number, number, number] {
  const m = /^\s*(\d+)\.(\d+)\.(\d+)/.exec(v);
  if (!m) return [0, 0, 0];
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

/**
 * Compare two semver-ish strings: `-1` if `a < b`, `0` if equal, `1` if `a > b`.
 * Malformed input on either side is treated as `0.0.0` — always returns a value,
 * never throws.
 */
export function semverCompare(a: string, b: string): -1 | 0 | 1 {
  const pa = parseParts(a);
  const pb = parseParts(b);
  for (let i = 0; i < 3; i++) {
    if (pa[i] > pb[i]) return 1;
    if (pa[i] < pb[i]) return -1;
  }
  return 0;
}

/** True when `a >= b` in semver order (see {@link semverCompare}). */
export function semverGte(a: string, b: string): boolean {
  return semverCompare(a, b) >= 0;
}
